import type { MillageInputsForm } from '~/builders/millage-info/MillageInfoInputs';
import { getMillagePhaseForYear, getTransitionPeriodInfo } from '../../utilities/millage-phase';
import type { LinearBuild } from './shape';

interface GenerateShapeParams {
  build: LinearBuild; // not used, but for consistency
  millageInputs: MillageInputsForm;
  startYear: number;
  endYear: number;
}

export function generateLinearShape({
  millageInputs,
  startYear,
  endYear,
}: GenerateShapeParams): Record<number, { taxYear: number; millage: number; isTunable: boolean }> {
  const totalYears = endYear - startYear + 1;

  // Get transition period info
  const transitionInfo = getTransitionPeriodInfo(startYear, totalYears, millageInputs);

  // Generate millage values for each tax year
  const result: Record<number, { taxYear: number; millage: number; isTunable: boolean }> = {};
  const years = Array.from({ length: totalYears }, (_, i) => startYear + i);

  years.forEach((year) => {
    const phaseInfo = getMillagePhaseForYear(year, startYear, totalYears, millageInputs);

    // If we're in peak or minimum phase, use the provided millage value
    if (phaseInfo.phase !== 'transition') {
      result[year] = {
        taxYear: year,
        millage: phaseInfo.millage,
        isTunable: false,
      };
      return;
    }

    // Calculate transition millage using a linear interpolation
    const yearsIntoTransition = year - transitionInfo.startYear;

    // Avoid division by zero if transition period is only one year long
    if (transitionInfo.length <= 1) {
      result[year] = {
        taxYear: year,
        millage: transitionInfo.peakMillage,
        isTunable: true,
      };
      return;
    }

    const transitionProgress = yearsIntoTransition / (transitionInfo.length - 1); // a value from 0 to 1

    const millageRange = transitionInfo.peakMillage - transitionInfo.minimumMillage;
    const millage = transitionInfo.peakMillage - transitionProgress * millageRange;

    const roundedMillage = Math.round(millage * 100) / 100;

    result[year] = {
      taxYear: year,
      millage: roundedMillage,
      isTunable: true,
    };
  });

  return result;
}
